import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '../../ThemeContext';

interface JsonViewerProps {
    data: unknown;
    maxHeight?: number | string;
    onJwtClick?: (jwt: string) => void;
}

// JWT regex: three base64url segments separated by dots, starting with eyJ
const JWT_REGEX = /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

const JsonViewer = ({ data, maxHeight = 300, onJwtClick }: JsonViewerProps) => {
    const { mode } = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);

    const colors = {
        key: mode === 'dark' ? '#9cdcfe' : '#0451a5',
        string: mode === 'dark' ? '#ce9178' : '#a31515',
        number: mode === 'dark' ? '#b5cea8' : '#098658',
        boolean: mode === 'dark' ? '#569cd6' : '#0000ff',
        null: mode === 'dark' ? '#569cd6' : '#0000ff',
        bracket: mode === 'dark' ? '#ffd700' : '#000000',
        colon: mode === 'dark' ? '#cccccc' : '#000000',
        jwt: mode === 'dark' ? '#4fc3f7' : '#0277bd',
    };

    const syntaxHighlight = (json: string): string => {
        // Escape HTML
        let escaped = json
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

        // Apply syntax highlighting
        escaped = escaped.replace(
            /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
            (match) => {
                let color = colors.number;
                if (/^"/.test(match)) {
                    if (/:$/.test(match)) {
                        // Key
                        color = colors.key;
                        const keyWithoutColon = match.slice(0, -1);
                        return `<span style="color:${color}">${keyWithoutColon}</span><span style="color:${colors.colon}">:</span>`;
                    }
                    // String value - check for JWT
                    const stringContent = match.slice(1, -1); // Remove quotes
                    if (JWT_REGEX.test(stringContent)) {
                        // Reset regex lastIndex
                        JWT_REGEX.lastIndex = 0;
                        // Wrap JWTs in clickable spans
                        const highlightedContent = stringContent.replace(
                            JWT_REGEX,
                            (jwt) => `</span><span class="jwt-token" data-jwt="${jwt}" style="color:${colors.jwt};text-decoration:underline;cursor:pointer">${jwt}</span><span style="color:${colors.string}">`,
                        );
                        return `<span style="color:${colors.string}">"${highlightedContent}"</span>`;
                    }
                    color = colors.string;
                } else if (/true|false/.test(match)) {
                    color = colors.boolean;
                } else if (/null/.test(match)) {
                    color = colors.null;
                }
                return `<span style="color:${color}">${match}</span>`;
            },
        );

        // Highlight brackets
        escaped = escaped.replace(/([[\]{}])/g, `<span style="color:${colors.bracket}">$1</span>`);

        return escaped;
    };

    // Handle JWT clicks
    useEffect(() => {
        if (!onJwtClick || !containerRef.current) return undefined;

        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('jwt-token')) {
                const jwt = target.getAttribute('data-jwt');
                if (jwt) {
                    onJwtClick(jwt);
                }
            }
        };

        const container = containerRef.current;
        container.addEventListener('click', handleClick);
        return () => container.removeEventListener('click', handleClick);
    }, [onJwtClick]);

    const jsonString = JSON.stringify(data, null, 2);
    const highlighted = syntaxHighlight(jsonString);

    return (
        <Box
            ref={containerRef}
            sx={{
                backgroundColor: mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
                borderRadius: 1,
                p: 2,
                maxHeight,
                overflow: 'auto',
                fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                fontSize: '0.8rem',
                lineHeight: 1.5,
                border: 1,
                borderColor: mode === 'dark' ? 'grey.800' : 'grey.300',
            }}
        >
            <pre
                style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                 
                dangerouslySetInnerHTML={{ __html: highlighted }}
            />
        </Box>
    );
};

export default JsonViewer;
